import { memo, useCallback, useContext, useMemo, useRef, FC } from 'react'
import styled from 'styled-components'
import { transparentize, cover } from 'polished'

import TouchDeviceContext from 'contexts/TouchDeviceContext'

import ChevronDownIcon from '../../../icons/ChevronDown'
import StreamableVideo from '../../../display/StreamableVideo'
import ResponsiveImage from '../../../display/ResponsiveImage'
import type { ResponsiveImageProperties } from '../../../display/ResponsiveImage/types'

import LinkOrA from '../LinkOrA'
import { Slide } from '../styled'

import EmblaSlider from './EmblaSlider'
import { getDefaultImageVersion } from './helpers'

interface ArrowProps {
  left?: boolean
  right?: boolean
  show?: boolean
  portrait?: boolean
}

export const Arrow = styled.button<ArrowProps>`
  position: absolute;
  height: ${({ portrait }) => (portrait ? '36px' : '30px')};
  width: ${({ portrait }) => (portrait ? '36px' : '30px')};
  padding: 0;

  top: 50%;
  transform: translateY(-50%);

  opacity: 0;
  transition: opacity 300ms ease-out;

  &:disabled {
    visibility: hidden;
  }

  ${({ left }) =>
    left &&
    `
    left: 8px;
    svg {
      margin-left: -2px;
    }
  `}

  ${({ right }) =>
    right &&
    `
    right: 8px;
    svg {
      margin-right: -2px;
    }
  `}

  ${({ show }) =>
    show &&
    `
    opacity: 1 !important;
  `}

  appearance: none;
  outline: 0;
  border: none;
  border-radius: 50%;
  background-color: ${({ theme }) => transparentize(0.3, theme.brand.forest)};

  svg {
    display: inline-block;
    vertical-align: middle;
    height: ${({ portrait }) => (portrait ? '13px' : '8px')};
    margin-top: -2px;
    color: white;
    transform: rotate(${({ left }) => (left ? '90deg' : '-90deg')});
  }
`

export const Wrapper = styled.div<{ asLink?: boolean }>`
  ${cover()}
  &:hover {
    ${Arrow} {
      opacity: 1 !important;
    }
  }

  ${({ asLink }) =>
    asLink &&
    `
    cursor: pointer;
  `}
`

export const SliderWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  z-index: 0;

  > div {
    height: 100%;
  }
`

type StreamableVideoType = Pick<GQL_Video, 'id' | 'streamUrl' | 'thumbnailUrl'>
type CardSliderPhoto = {
  image: ResponsiveImageProperties
}

type CardSliderProps = {
  title: string
  photos?: CardSliderPhoto[]
  videos?: StreamableVideoType[]
  url?: string
  asLink?: boolean
  showArrows?: boolean
  portrait?: boolean
}

const CardSlider = memo(
  ({
    title,
    photos = [],
    videos = [],
    url,
    asLink,
    showArrows,
    portrait,
  }: CardSliderProps) => {
    const wrapperRef = useRef(null)
    const scrollPrevRef = useRef<() => void>(() => {})
    const scrollNextRef = useRef<() => void>(() => {})

    const isTouchDevice = useContext(TouchDeviceContext)

    const onScrollPrev = useCallback((cb: () => void) => {
      scrollPrevRef.current = cb
    }, [])

    const onScrollNext = useCallback((cb: () => void) => {
      scrollNextRef.current = cb
    }, [])

    type SlideData = {
      isActive?: boolean
    }

    const keyedSlides: Array<{
      Component: FC<SlideData>
      key: string
    }> = useMemo(
      () => [
        ...videos.map(
          ({ id, streamUrl, thumbnailUrl: originalThumbnailUrl }, index) => {
            // when a video is the first slide, its poster should be the story cover photo
            const thumbnailUrl =
              index === 0
                ? getDefaultImageVersion(photos[0])
                : originalThumbnailUrl

            return {
              Component: ({ isActive }: SlideData) => (
                <StreamableVideo
                  src={streamUrl}
                  poster={thumbnailUrl}
                  forcePause={!isActive}
                />
              ),
              key: `video-${id}`,
            }
          }
        ),
        ...photos.map((photo, index) => {
          const alt = `Photo ${index + 1} for ${title}`
          return {
            Component: () => (
              <Slide>
                <LinkOrA asLink={asLink} to={url}>
                  <ResponsiveImage
                    {...photo.image}
                    zIndex={1}
                    alt={alt}
                    cover
                  />
                </LinkOrA>
              </Slide>
            ),
            key: `photo-${getDefaultImageVersion(photo)}`,
          }
        }),
      ],
      [photos?.length, videos?.length]
    )

    if (photos?.length === 0) {
      return null
    }

    const arrows = !isTouchDevice ? (
      <>
        <Arrow
          aria-label="Previous"
          onClick={() => scrollPrevRef.current()}
          show={showArrows}
          left
          portrait={portrait}
        >
          <ChevronDownIcon />
        </Arrow>
        <Arrow
          aria-label="Next"
          onClick={() => scrollNextRef.current()}
          show={showArrows}
          right
          portrait={portrait}
        >
          <ChevronDownIcon />
        </Arrow>
      </>
    ) : null

    return (
      <Wrapper ref={wrapperRef}>
        <SliderWrapper>
          <EmblaSlider
            title={title}
            keyedSlides={keyedSlides}
            arrows={arrows}
            portrait={portrait}
            onScrollPrev={onScrollPrev}
            onScrollNext={onScrollNext}
          />
        </SliderWrapper>
      </Wrapper>
    )
  },
  (prevProps, props) => {
    const samePhotoCount = prevProps?.photos?.length === props?.photos?.length
    const sameVideoCount = prevProps?.videos?.length === props?.videos?.length
    const sameUrl = prevProps?.url === props?.url
    const sameShowArrows = prevProps?.showArrows === props?.showArrows

    return samePhotoCount && sameVideoCount && sameUrl && sameShowArrows
  }
)

export default CardSlider
